<template>
  <div>
    <!-- Standard Name -->
    <div class="form-group">
      <label for="standard_name">Standard Name <span class="text-danger">*</span></label>
      <input
        v-model="name"
        type="text"
        id="standard_name"
        class="form-control"
        placeholder="e.g., My Tournament 2026"
        maxlength="255"
      />
      <p class="help-block text-gray-600">Give this standard a descriptive name so you can identify it later.</p>
    </div>

    <hr />

    <!-- Age Divisions -->
    <div class="m-b-md">
      <div class="flex justify-space-between align-items-center m-b-sm">
        <h3 class="m-b-none">Age Divisions</h3>
        <button class="btn btn-default" @click="addDivision">
          <i class="fa-solid fa-plus"></i> Add Division
        </button>
      </div>
      <p class="text-gray-600 m-b-sm">
        Define age/grade divisions. Each division must have either an age range or grade range (or both).
      </p>
    </div>

    <div v-if="visibleDivisions.length === 0" class="alert alert-warning">
      No divisions defined. Click "Add Division" to create your first division.
    </div>

    <div v-for="(division, dIdx) in ageDivisions" :key="dIdx" class="m-b-md">
      <div v-if="!division._destroy" class="border border-solid border-gray-300 rounded-xs">
        <!-- Division Header -->
        <div
          class="p-sm bg-gray-50 border-b border-solid border-gray-300 cursor-pointer flex justify-space-between align-items-center"
          @click="toggleDivision(dIdx)"
        >
          <div class="flex align-items-center gap-sm">
            <i :class="[expandedDivision === dIdx ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right']"></i>
            <span class="font-semi">{{ division.name || 'New Division' }}</span>
            <span v-if="division.gender && division.gender !== 'any'" class="label label-default">
              {{ capitalizeGender(division.gender) }}
            </span>
            <span v-if="divisionEligibilityPreview(division)" class="text-gray-600 text-14">
              {{ divisionEligibilityPreview(division) }}
            </span>
          </div>
          <button class="btn btn-xs btn-danger" @click.stop="removeDivision(dIdx)">
            <i class="fa-solid fa-trash-can"></i> Remove
          </button>
        </div>

        <!-- Division Body -->
        <div v-show="expandedDivision === dIdx" class="p-sm">
          <div class="row">
            <div class="col-xs-12 col-md-6">
              <div class="form-group">
                <label>Division Name <span class="text-danger">*</span></label>
                <input
                  v-model="division.name"
                  type="text"
                  class="form-control"
                  placeholder="e.g., K-3 Elementary, Middle School, 14U"
                />
              </div>
            </div>
            <div class="col-xs-12 col-md-3">
              <div class="form-group">
                <label>Gender</label>
                <select v-model="division.gender" class="form-control">
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div class="col-xs-12 col-md-3">
              <div class="form-group">
                <label>Display Order</label>
                <input v-model.number="division.display_order" type="number" class="form-control" min="0" />
              </div>
            </div>
          </div>

          <!-- Age Range Section -->
          <div class="p-sm bg-gray-50 rounded-xs m-b-sm">
            <h5 class="m-t-none m-b-sm">Age Criteria <span class="text-gray-600 font-normal">(optional if using grades)</span></h5>
            <div class="row">
              <div class="col-xs-6">
                <div class="form-group m-b-none">
                  <label>Min Age</label>
                  <input
                    v-model.number="division.min_age"
                    type="number"
                    class="form-control"
                    min="1"
                    max="99"
                    placeholder="-"
                  />
                </div>
              </div>
              <div class="col-xs-6">
                <div class="form-group m-b-none">
                  <label>Max Age</label>
                  <input
                    v-model.number="division.max_age"
                    type="number"
                    class="form-control"
                    min="1"
                    max="99"
                    placeholder="-"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Grade Range Section -->
          <div class="p-sm bg-gray-50 rounded-xs m-b-sm">
            <h5 class="m-t-none m-b-sm">Grade Criteria <span class="text-gray-600 font-normal">(optional if using ages)</span></h5>
            <div class="row">
              <div class="col-xs-6">
                <div class="form-group m-b-none">
                  <label>Min Grade</label>
                  <select v-model="division.min_grade" class="form-control">
                    <option :value="null">-</option>
                    <option v-for="grade in gradeOptions" :key="grade.value" :value="grade.value">
                      {{ grade.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-xs-6">
                <div class="form-group m-b-none">
                  <label>Max Grade</label>
                  <select v-model="division.max_grade" class="form-control">
                    <option :value="null">-</option>
                    <option v-for="grade in gradeOptions" :key="grade.value" :value="grade.value">
                      {{ grade.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Eligibility Note -->
          <div class="form-group">
            <label>Eligibility Note</label>
            <textarea
              v-model="division.eligibility_note"
              class="form-control"
              rows="2"
              placeholder="Optional notes about eligibility requirements (e.g., 'Must be enrolled in grades 9-12')"
            ></textarea>
          </div>

          <!-- Weight Classes -->
          <div class="p-sm border border-solid border-gray-200 rounded-xs">
            <h5 class="m-t-none m-b-sm">Weight Classes</h5>

            <!-- Current weights -->
            <div v-if="activeWeightClasses(division).length > 0" class="m-b-sm">
              <div class="flex flex-wrap gap-xs">
                <span
                  v-for="wc in sortedWeightClasses(division)"
                  :key="wc.weight_value"
                  class="label label-default cursor-pointer"
                  style="font-size: 13px;"
                  @click="removeWeightClass(division, wc)"
                >
                  {{ wc.display_name || wc.weight_value + ' lbs' }}
                  <i class="fa-solid fa-xmark m-l-xxs"></i>
                </span>
              </div>
              <p class="text-12 text-gray-600 m-t-xxs m-b-none">Click a weight to remove it.</p>
            </div>

            <!-- Add weight input -->
            <div class="flex gap-xs align-items-end m-b-sm">
              <div class="form-group m-b-none" style="width: 120px;">
                <label class="text-12">Add Weight</label>
                <input
                  v-model.number="newWeightValue[dIdx]"
                  type="number"
                  class="form-control"
                  placeholder="lbs"
                  min="1"
                  @keyup.enter="addWeightClass(division, dIdx)"
                />
              </div>
              <button class="btn btn-default btn-sm" @click="addWeightClass(division, dIdx)">
                Add
              </button>
            </div>

            <!-- Quick add buttons -->
            <div class="m-b-xs">
              <span class="text-12 text-gray-600">Quick add USAW weights:</span>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_8u')">
                8U
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_10u')">
                10U
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_12u')">
                12U
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_14u')">
                14U
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_16u')">
                16U
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'usaw_junior')">
                Junior
              </button>
            </div>
            <div>
              <span class="text-12 text-gray-600">Quick add NFHS weights:</span>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'nfhs_boys')">
                Boys
              </button>
              <button class="btn btn-xs btn-link" @click="addWeightSet(division, 'nfhs_girls')">
                Girls
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger m-t-md">
      {{ errorMessage }}
    </div>

    <!-- Save/Cancel Footer -->
    <div class="flex gap-sm m-t-lg p-t-md border-t border-solid border-gray-200">
      <ladda-button @lbClicked="save" el-class="btn-primary" :loading="saving">
        {{ editing ? 'Update Standard' : 'Create Standard' }}
      </ladda-button>
      <a :href="backUrl" class="btn btn-default">Cancel</a>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'division-standard-form',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    existingStandard: {
      type: Object,
      default: null
    },
    backUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      name: '',
      ageDivisions: [],
      saving: false,
      expandedDivision: 0,
      newWeightValue: {},
      gradeOptions: [
        { value: -1, label: 'Pre-K' },
        { value: 0, label: 'Kindergarten' },
        { value: 1, label: '1st Grade' },
        { value: 2, label: '2nd Grade' },
        { value: 3, label: '3rd Grade' },
        { value: 4, label: '4th Grade' },
        { value: 5, label: '5th Grade' },
        { value: 6, label: '6th Grade' },
        { value: 7, label: '7th Grade' },
        { value: 8, label: '8th Grade' },
        { value: 9, label: '9th Grade' },
        { value: 10, label: '10th Grade' },
        { value: 11, label: '11th Grade' },
        { value: 12, label: '12th Grade' },
        { value: 99, label: 'Other' }
      ],
      weightSets: {
        'usaw_8u': [40, 43, 46, 49, 52, 56, 62, 70, 85],
        'usaw_10u': [49, 52, 55, 58, 62, 66, 71, 77, 85, 100, 120],
        'usaw_12u': [58, 63, 68, 73, 78, 83, 88, 95, 102, 110, 120, 130, 145, 160],
        'usaw_14u': [75, 80, 85, 90, 95, 100, 105, 110, 117, 125, 133, 142, 152, 165, 185, 210, 225],
        'usaw_16u': [88, 94, 100, 106, 113, 120, 126, 132, 138, 145, 152, 160, 170, 182, 195, 220, 285],
        'usaw_junior': [100, 106, 113, 120, 126, 132, 138, 145, 152, 160, 170, 182, 195, 220, 285],
        'nfhs_boys': [106, 113, 120, 126, 132, 138, 144, 150, 157, 165, 175, 190, 215, 285],
        'nfhs_girls': [100, 105, 110, 115, 120, 125, 130, 135, 140, 145, 155, 170, 190, 235]
      }
    };
  },
  computed: {
    editing() {
      return !!this.existingStandard;
    },
    visibleDivisions() {
      return this.ageDivisions.filter(d => !d._destroy);
    }
  },
  mounted() {
    if (this.existingStandard) {
      this.name = this.existingStandard.name;
      this.ageDivisions = this.existingStandard.age_divisions.map(div => ({
        id: div.id,
        name: div.name,
        gender: div.gender || 'any',
        min_age: div.min_age,
        max_age: div.max_age,
        min_grade: div.min_grade,
        max_grade: div.max_grade,
        eligibility_note: div.eligibility_note || '',
        display_order: div.display_order || 0,
        weight_classes_attributes: (div.weight_classes || []).map(wc => ({
          id: wc.id,
          weight_value: wc.weight_value,
          display_name: wc.display_name,
          display_order: wc.display_order || 0
        }))
      }));
      if (this.ageDivisions.length > 0) {
        this.expandedDivision = 0;
      }
    }
  },
  methods: {
    capitalizeGender(gender) {
      if (!gender || gender === 'any') return '';
      return gender.charAt(0).toUpperCase() + gender.slice(1);
    },
    toggleDivision(idx) {
      this.expandedDivision = this.expandedDivision === idx ? null : idx;
    },
    addDivision() {
      const newIdx = this.ageDivisions.length;
      this.ageDivisions.push({
        id: null,
        _destroy: false,
        name: '',
        gender: 'any',
        min_age: null,
        max_age: null,
        min_grade: null,
        max_grade: null,
        eligibility_note: '',
        display_order: newIdx,
        weight_classes_attributes: []
      });
      this.expandedDivision = newIdx;
    },
    removeDivision(idx) {
      const division = this.ageDivisions[idx];
      if (division.id) {
        this.$set(division, '_destroy', true);
      } else {
        this.ageDivisions.splice(idx, 1);
      }
      // Collapse if we removed the expanded one
      if (this.expandedDivision === idx) {
        this.expandedDivision = null;
      }
    },
    divisionEligibilityPreview(division) {
      const parts = [];
      if (division.min_age && division.max_age) {
        parts.push(`Ages ${division.min_age}-${division.max_age}`);
      }
      if (division.min_grade !== null && division.max_grade !== null) {
        const minLabel = this.gradeOptions.find(g => g.value === division.min_grade)?.label || '';
        const maxLabel = this.gradeOptions.find(g => g.value === division.max_grade)?.label || '';
        if (minLabel && maxLabel) {
          parts.push(`${minLabel.replace(' Grade', '')}-${maxLabel.replace(' Grade', '')}`);
        }
      }
      return parts.join(', ');
    },
    activeWeightClasses(division) {
      if (!division.weight_classes_attributes) return [];
      return division.weight_classes_attributes.filter(wc => !wc._destroy);
    },
    sortedWeightClasses(division) {
      return this.activeWeightClasses(division).slice().sort((a, b) => a.weight_value - b.weight_value);
    },
    addWeightClass(division, divisionIndex) {
      const weight = this.newWeightValue[divisionIndex];
      if (!weight || weight <= 0) return;

      if (!division.weight_classes_attributes) {
        this.$set(division, 'weight_classes_attributes', []);
      }

      // Check for duplicate
      const exists = this.activeWeightClasses(division).some(wc => wc.weight_value === weight);
      if (exists) {
        this.$set(this.newWeightValue, divisionIndex, null);
        return;
      }

      division.weight_classes_attributes.push({
        id: null,
        weight_value: weight,
        display_order: division.weight_classes_attributes.length
      });

      this.$set(this.newWeightValue, divisionIndex, null);
    },
    removeWeightClass(division, weightClass) {
      if (weightClass.id) {
        this.$set(weightClass, '_destroy', true);
      } else {
        const idx = division.weight_classes_attributes.indexOf(weightClass);
        if (idx > -1) {
          division.weight_classes_attributes.splice(idx, 1);
        }
      }
    },
    addWeightSet(division, setName) {
      const weights = this.weightSets[setName];
      if (!weights) return;

      if (!division.weight_classes_attributes) {
        this.$set(division, 'weight_classes_attributes', []);
      }

      const existingWeights = this.activeWeightClasses(division).map(wc => wc.weight_value);
      const startOrder = division.weight_classes_attributes.length;

      weights.forEach((w, idx) => {
        if (!existingWeights.includes(w)) {
          division.weight_classes_attributes.push({
            id: null,
            weight_value: w,
            display_order: startOrder + idx
          });
        }
      });
    },
    buildParams() {
      return {
        division_standard: {
          name: this.name,
          age_divisions_attributes: this.ageDivisions.map((d, idx) => ({
            id: d.id || undefined,
            _destroy: d._destroy || undefined,
            name: d.name,
            gender: d.gender,
            min_age: d.min_age || null,
            max_age: d.max_age || null,
            min_grade: d.min_grade,
            max_grade: d.max_grade,
            eligibility_note: d.eligibility_note || null,
            display_order: idx,
            weight_classes_attributes: (d.weight_classes_attributes || []).map((wc, wIdx) => ({
              id: wc.id || undefined,
              _destroy: wc._destroy || undefined,
              weight_value: wc.weight_value,
              display_name: wc.display_name || null,
              display_order: wIdx
            }))
          }))
        }
      };
    },
    async save() {
      this.saving = true;
      this.clearError();

      const url = this.editing
        ? this.$apiService.divisionStandardUrl(this.existingStandard.id)
        : this.$apiService.divisionStandardsUrl();

      const method = this.editing ? 'put' : 'post';

      try {
        await axios[method](url, this.buildParams());
        window.location.assign(this.backUrl);
      } catch (error) {
        if (error.response?.data) {
          this.showError(this.parseErrorResponse(error));
        } else {
          this.showError(error.message || 'An unexpected error occurred. Please try again.');
        }
        window.scrollTo(0, 0);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
